import { GameType } from '../persistence/types.js';
import { Position } from './GridGeometry.js';

/**
 * Grid-generation rules split into **generic** (always applied — they keep every grid
 * legible and fair) and **optional** (each exists for one or more specific games, and is
 * only worth enforcing when that game is in the set being generated for).
 *
 * This module owns the optional rules: which games require each, whether they apply to a
 * given set, and whole-grid predicates to detect a finished grid that breaks one. The
 * generator ({@link GridGenerator}) enforces them incrementally during placement; the
 * duplicate flow uses the predicates here to warn when adding a game whose rule an existing
 * grid already breaks. See docs/generation.md "Rule Ownership".
 *
 * The generic rules (corner 2×2, min distance 3, edge/row/column limits, corner proximity,
 * no consecutive digits sharing a row/column) are not modelled here: they never relax, so a
 * generated grid always satisfies them and they can never be the reason a game is at risk.
 */
export type OptionalRule =
    | 'topRow'                 // no digit in row 0
    | 'noStrictDecreasingRows' // digits not all strictly bottom→top increasing
    | 'centerSquare'           // no digit in the centre 2×2
    | 'oppositeEdge'           // no opposite-edge pair sharing a line (wrap-adjacent)
    | 'quarterPosition';       // at most 2 digits per quarter-position

/** The games each optional rule is required by. A rule applies iff the set includes one. */
export const RULE_OWNERS: Record<OptionalRule, GameType[]> = {
    topRow: ['flood', 'deluge'],
    noStrictDecreasingRows: ['flood', 'deluge'],
    centerSquare: ['orbits', 'ignition'],
    oppositeEdge: ['crossing'],
    quarterPosition: ['jumps'],
};

const ALL_RULES = Object.keys(RULE_OWNERS) as OptionalRule[];

export type ActiveRules = Record<OptionalRule, boolean>;

/**
 * Which optional rules apply when generating for `games`. Passing `undefined` turns them
 * **all on** — the most-restrictive default, yielding a grid valid for every game (used by
 * the plain `new GridGenerator(config)` callers in cli and tests).
 */
export function activeRules(games?: Iterable<GameType>): ActiveRules {
    if (games === undefined) {
        return Object.fromEntries(ALL_RULES.map(r => [r, true])) as ActiveRules;
    }
    const set = new Set(games);
    return Object.fromEntries(
        ALL_RULES.map(r => [r, RULE_OWNERS[r].some(g => set.has(g))])
    ) as ActiveRules;
}

// ── Whole-grid predicates ──────────────────────────────────────────────────
// Each returns true when the grid BREAKS the rule.

function breaksTopRow(positions: Position[]): boolean {
    return positions.some(p => p.row === 0);
}

function breaksCenterSquare(positions: Position[], size: number): boolean {
    const lo = Math.floor(size / 2) - 1;
    return positions.some(p => (p.row === lo || p.row === lo + 1) && (p.col === lo || p.col === lo + 1));
}

/**
 * A digit on each of two opposite edges of the same line: the grid wraps, so the pair sits
 * adjacent across the seam. Both axes — left/right sharing a row, top/bottom sharing a
 * column (the column case is unreachable while the top-row rule holds, but real once it is
 * relaxed for a set without Flood/Deluge).
 */
export function breaksOppositeEdge(positions: Position[], size: number): boolean {
    const last = size - 1;
    for (let i = 0; i < positions.length; i++) {
        for (let j = i + 1; j < positions.length; j++) {
            const a = positions[i], b = positions[j];
            const horiz = a.row === b.row
                && ((a.col === 0 && b.col === last) || (a.col === last && b.col === 0));
            const vert = a.col === b.col
                && ((a.row === 0 && b.row === last) || (a.row === last && b.row === 0));
            if (horiz || vert) return true;
        }
    }
    return false;
}

function breaksQuarterPosition(positions: Position[], size: number): boolean {
    if (size % 2 !== 0) return false;  // odd grids have no quarters
    const half = size / 2;
    const counts = new Map<string, number>();
    for (const p of positions) {
        const key = `${p.row % half},${p.col % half}`;
        const n = (counts.get(key) ?? 0) + 1;
        if (n >= 3) return true;
        counts.set(key, n);
    }
    return false;
}

/**
 * Digits strictly decreasing in row from min to max (min at the bottom, max at the top),
 * which makes Flood/Deluge trivially solvable. `digitsByValue` is the digit positions in
 * ascending value order.
 */
function breaksNoStrictDecreasingRows(digitsByValue: Position[]): boolean {
    for (let i = 0; i + 1 < digitsByValue.length; i++) {
        if (digitsByValue[i].row <= digitsByValue[i + 1].row) return false;
    }
    return digitsByValue.length > 1;
}

/**
 * Optional rules a finished grid breaks. `digitsByValue` must be the digit positions in
 * ascending digit-value order (the ordering rule needs it); the others are order-agnostic.
 */
export function violatedOptionalRules(digitsByValue: Position[], size: number): Set<OptionalRule> {
    const broken = new Set<OptionalRule>();
    if (breaksTopRow(digitsByValue)) broken.add('topRow');
    if (breaksNoStrictDecreasingRows(digitsByValue)) broken.add('noStrictDecreasingRows');
    if (breaksCenterSquare(digitsByValue, size)) broken.add('centerSquare');
    if (breaksOppositeEdge(digitsByValue, size)) broken.add('oppositeEdge');
    if (breaksQuarterPosition(digitsByValue, size)) broken.add('quarterPosition');
    return broken;
}

/**
 * Games in `games` that require a rule this grid breaks — i.e. adding one of them risks an
 * unsolvable or degenerate grid. Sorted in the order given. `digitsByValue` is the digit
 * positions in ascending value order.
 */
export function gamesAtRisk(
    digitsByValue: Position[],
    size: number,
    games: Iterable<GameType>,
): GameType[] {
    const broken = violatedOptionalRules(digitsByValue, size);
    if (broken.size === 0) return [];
    const atRisk: GameType[] = [];
    for (const g of games) {
        const owns = ALL_RULES.some(r => broken.has(r) && RULE_OWNERS[r].includes(g));
        if (owns && !atRisk.includes(g)) atRisk.push(g);
    }
    return atRisk;
}
